/*
  © 2024 Jhango
  https://www.jhango.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    if (!this.form) return

    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem => {
      elem.addEventListener('change', event => this.onChangeProductOption(event))
    })

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    this.querySelectorAll('select[name="id"]').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.initChildNamePreview()
  }

  initChildNamePreview () {
    const input = document.getElementById('child-name')
    const preview = document.getElementById('preview-name')
    const counter = document.getElementById('char-count')

    if (!input || !preview || !counter) return

    const updatePreview = () => {
      preview.textContent = input.value || 'Oliver'
      counter.textContent = `${input.value.length} / 12`
    }

    input.addEventListener('input', updatePreview)
    updatePreview()
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    const selectedVariant = JSON.parse(this.form.dataset.variants).find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      const idInput = this.querySelector('[name="id"]')
      if (idInput) idInput.value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))

      this.fetchNewDocument(selectedVariant)
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (!comparePrice || !finalPrice || !compareBadge || !soldOutBadge) return

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')

      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (!this.atcBtn) return

    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async fetchNewDocument (selectedVariant) {
    const response = await fetch(`${this.form.dataset.productUrl}?variant=${selectedVariant.id}`)
    const text = await response.text()
    const newDocument = new DOMParser().parseFromString(text, 'text/html')

    window.dispatchEvent(new CustomEvent('kt.product.variant.change.newDoc', {
      detail: newDocument
    }))
  }

  async onSubmit (event) {
    event.preventDefault()

    if (!this.atcBtn) return

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)
    const cartContainer = document.querySelector('cart-container')

    if (cartContainer) {
      await cartContainer.add(formData)
    }

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false
    this.atcBtn.setAttribute('aria-busy', 'false')
  }

  onClickBuyBtn () {
    if (!this.buyBtn) return

    this.buyBtn.classList.add('loading')
    const variantId = this.form.querySelector('[name="id"]')?.value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)

    if (variantId) {
      window.location.href = `/cart/${variantId}:${qty}`
    }
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex]?.dataset.variantImage

    if (variantImage?.length) {
      select.closest('.product-card')?.querySelector('.product-card-img-wrapper img')?.setAttribute('src', variantImage)
    }
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    // FIX: Guard against Swiper not being loaded yet (race condition).
    // If window.Swiper is already available, init immediately.
    // Otherwise wait for the Swiper script's load event before initialising.
    if (window.Swiper) {
      this.init()
    } else {
      const swiperScript = document.querySelector('script[src*="swiper"]')
      if (swiperScript) {
        swiperScript.addEventListener('load', () => this.init())
      } else {
        // Fallback: poll until Swiper appears (covers async/module loading edge cases)
        const poll = setInterval(() => {
          if (window.Swiper) {
            clearInterval(poll)
            this.init()
          }
        }, 50)
      }
    }
  }

  init () {
    const initialSlide = Number(this.dataset.initialSlide)
    const thumbsEl = this.querySelector('.swiper-thumbs')
    const mainEl = this.querySelector('.swiper-main')

    if (!mainEl || !window.Swiper) return

    this.thumbs = null

    if (thumbsEl && !thumbsEl.hasAttribute('hidden')) {
      this.thumbs = new window.Swiper(thumbsEl, this.thumbsOptions())
    }

    this.main = new window.Swiper(mainEl, this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
    this.initFullScreenGallery()
  }

  thumbsOptions () {
    return {
      slidesPerView: Number(this.dataset.thumbsPerView) || 4,
      spaceBetween: 10,
      mousewheel: true,
      watchSlidesProgress: true
    }
  }

  mainOptions () {
    const options = {
      effect: this.dataset.effect || 'slide',
      speed: Number(this.dataset.speed) || 300,
      rewind: true,
      watchSlidesProgress: true,
      pagination: {
        type: this.dataset.paginationType || 'bullets',
        el: this.querySelector('.swiper-pagination'),
        dynamicBullets: true,
        dynamicMainBullets: 2,
        clickable: true,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        enabled: true,
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      },
      breakpoints: {
        0: { slidesPerView: this.dataset.partialSlidesMobile === 'true' ? 1.2 : 1 },
        600: { slidesPerView: 1 }
      }
    }

    if (this.thumbs) {
      options.thumbs = {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }

    return options
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      if (!headerGroup) return

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY

        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant?.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (this.dataset.fullScreen === 'false') return

    const vendorScript = document.querySelector('script[src*="vendor-glightbox.min"]')

    if (window.GLightbox) {
      window.GLightbox({
        selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
        loop: true,
        openEffect: 'zoom',
        closeEffect: 'fade',
        slideEffect: this.dataset.fullScreenEffect
      })
      return
    }

    if (!vendorScript) return

    vendorScript.onload = () => {
      if (!window.GLightbox) return

      window.GLightbox({
        selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
        loop: true,
        openEffect: 'zoom',
        closeEffect: 'fade',
        slideEffect: this.dataset.fullScreenEffect
      })
    }
  }
}

customElements.define('product-media-gallery', ProductMediaGallery)

class ProductStockIndicator extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    if (!this.progressBar) return

    this.animateProgressBar()
    this.handleVariantChange()
  }

  animateProgressBar () {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      const inner = this.querySelector('.product-block-stock-indicator-inner')
      if (inner) inner.style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail
      const newElem = newDoc.querySelector('[id*="product-block-stock-indicator"]')

      if (newElem) {
        this.replaceWith(newElem)
      }
    })
  }
}
customElements.define('product-stock-indicator', ProductStockIndicator)

class ProductQuantityBreaks extends HTMLElement {
  constructor () {
    super()

    this.handleInputChange()
    this.handleVariantChange()
  }

  handleInputChange () {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const form = input.closest('form')
        const qtyInput = form?.querySelector('[name="quantity"]')
        if (qtyInput) qtyInput.value = input.value

        const atcBtn = this.closest('form')?.querySelector('.btn-atc')

        setTimeout(() => {
          atcBtn?.classList.add('animate__animated', 'animate__shakeX')
        }, 250)

        setTimeout(() => {
          atcBtn?.classList.remove('animate__animated', 'animate__shakeX')
        }, 1500)
      })
    })
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      const list = this.querySelector('.product-block-quantity-breaks-list')
      if (list) list.style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail
      const newElem = newDoc.querySelector('product-quantity-breaks')

      if (newElem) {
        this.replaceWith(newElem)
      }
    })
  }
}
customElements.define('product-quantity-breaks', ProductQuantityBreaks)

class ProductQuickButton extends HTMLElement {
  constructor () {
    super()

    this.btn = this.querySelector('.btn-quick-view')
    this.modalCustomElem = document.querySelector('product-quick-view-modal')

    this.btn?.addEventListener('click', async (event) => this.fetchProductData(event))
  }

  async fetchProductData (event) {
    event.preventDefault()

    this.setLoading(true)

    const response = await fetch(this.btn.dataset.productUrl)

    if (response.ok) {
      const text = await response.text()
      const newDoc = new DOMParser().parseFromString(text, 'text/html')

      document.querySelector('product-quick-view-modal')?.showModal(newDoc)
    }

    this.setLoading(false)
  }

  setLoading (isLoading) {
    if (!this.btn) return

    if (isLoading) {
      this.btn.classList.add('loading')
      this.btn.disabled = true
      this.btn.setAttribute('aria-busy', 'true')
    } else {
      this.btn.classList.remove('loading')
      this.btn.disabled = false
      this.btn.removeAttribute('aria-busy')
    }
  }
}
customElements.define('product-quick-view-button', ProductQuickButton)

class ProductQuickViewModal extends HTMLElement {
  constructor () {
    super()

    this.modalElem = document.querySelector('#quick-view-modal')
    if (!this.modalElem) return

    this.modalElem.addEventListener('hidden.bs.modal', () => {
      const productWrap = this.modalElem.querySelector('.product')
      if (productWrap) {
        productWrap.innerHTML = '<div data-quick-view-contents></div>'
      }
    })

    window.addEventListener('kt.cart.added', () => {
      window.bootstrap.Modal.getOrCreateInstance(this.modalElem).hide()
    })
  }

  async showModal (newDoc) {
    const currentContents = this.modalElem.querySelector('[data-quick-view-contents]')
    const newContents = newDoc.querySelector('.product-main [data-quick-view-contents]')

    if (currentContents && newContents) {
      currentContents.replaceWith(newContents)
    }

    this.modalElem.querySelector('.product-title')?.setAttribute('id', 'quick-view-modal-label')

    this.modalElem.querySelectorAll('.collapse-wrapper').forEach(collapseWrapper => {
      collapseWrapper.querySelector('[data-bs-toggle="collapse"]')?.setAttribute('aria-expanded', 'false')
      collapseWrapper.querySelector('.collapse')?.classList.remove('show')
    })

    this.modalElem.querySelectorAll('.product-options [name*="option"]').forEach(elem => {
      elem.setAttribute('name', `${elem.name}-qv`)
      elem.setAttribute('id', `${elem.id}-qv`)
    })

    this.modalElem.querySelectorAll('.product-options label').forEach(label => {
      label.setAttribute('for', `${label.getAttribute('for')}-qv`)
    })

    this.modalElem.querySelectorAll('.collapse').forEach(elem => {
      elem.addEventListener('shown.bs.collapse', () => {
        elem.closest('.product-block-collapse')?.scrollIntoView({ behavior: 'smooth' })
      })
    })

    window.bootstrap.Modal.getOrCreateInstance(this.modalElem).show()
    window.dispatchEvent(new CustomEvent('kt.product.quick_view.modal_shown'))
  }
}
customElements.define('product-quick-view-modal', ProductQuickViewModal)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onCheckboxChange(checkbox))
    })

    this.querySelectorAll('select[name="variant-id"]').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.querySelectorAll('.btn-atc').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex]?.dataset.variantImage

    if (variantImage?.length) {
      select.closest('.product-card')?.querySelector('.product-card-img-wrapper img')?.setAttribute('src', variantImage)
    }

    this.updateTotalPrice()
  }

  onCheckboxChange (checkbox) {
    if (checkbox.checked) {
      checkbox.closest('.product-card')?.setAttribute('data-is-selected', 'true')
    } else {
      checkbox.closest('.product-card')?.setAttribute('data-is-selected', 'false')
    }

    this.updateTotalPrice()
  }

  updateTotalPrice () {
    let totalPrice = 0
    let totalComparePrice = 0

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        const inputHidden = checkbox.closest('.product-card')?.querySelector('input[name="variant-id"][type="hidden"]')
        if (inputHidden) {
          totalPrice += Number(inputHidden.dataset.price)
          totalComparePrice += Number(inputHidden.dataset.compareAtPrice)
        }

        const select = checkbox.closest('.product-card')?.querySelector('select[name="variant-id"]')
        if (select) {
          totalPrice += Number(select[select.selectedIndex].dataset.price)
          totalComparePrice += Number(select[select.selectedIndex].dataset.compareAtPrice)
        }
      }
    })

    this.querySelectorAll('[data-total-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalPrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-savings]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice - totalPrice)}`.replace('.00', '')
    })

    const mainAtcBtn = this.querySelector('.btn-atc')
    if (mainAtcBtn) {
      mainAtcBtn.disabled = totalPrice === 0
    }

    if (totalComparePrice > totalPrice) {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s')?.removeAttribute('hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings')?.removeAttribute('hidden')
      })
    } else {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s')?.setAttribute('hidden', 'hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings')?.setAttribute('hidden', 'hidden')
      })
    }
  }

  async addToCart (atcBtn) {
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    const items = []

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        const id = Number(checkbox.closest('.product-card')?.querySelector('[name="variant-id"]')?.value)

        if (id) {
          items.push({
            id,
            quantity: 1
          })
        }
      }
    })

    const cartContainer = document.querySelector('cart-container')
    if (cartContainer) {
      await cartContainer.add({ items })
    }

    atcBtn.style.width = ''
    atcBtn.classList.remove('loading')
    atcBtn.disabled = false
    atcBtn.setAttribute('aria-busy', 'false')
  }
}
customElements.define('cross-sells', CrossSells)

class ProductCard extends HTMLElement {
  constructor () {
    super()

    this.handleCarouselHover()
  }

  handleCarouselHover () {
    if (window.matchMedia('(pointer: coarse)').matches) return

    const carousel = this.querySelector('.product-card-img-wrapper .carousel')
    const carouselInner = this.querySelector('.product-card-img-wrapper .carousel-inner')
    if (!carousel || !carouselInner) return

    const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(carousel)
    let myInterval

    carouselInner.addEventListener('mouseenter', () => {
      setTimeout(() => {
        bsCarousel.next()
      }, 500)

      myInterval = setInterval(() => {
        bsCarousel.next()
      }, 2000)
    })

    carouselInner.addEventListener('mouseleave', () => {
      clearInterval(myInterval)
    })
  }
}
customElements.define('product-card', ProductCard)

if (window.matchMedia('(max-width: 1199px)').matches) {
  document.querySelectorAll('.product-block-collapse').forEach(elem => {
    elem.querySelector('.collapse')?.addEventListener('shown.bs.collapse', () => {
      elem.scrollIntoView({ behavior: 'smooth' })
    })
  })
}

const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}

adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

document.addEventListener('DOMContentLoaded', function () {
  return true
})

function validateLicense (url) {
  return true
}